import React, { useState } from "react";
import { Wrench, Loader2 } from "lucide-react";

/* LT-19d/e — the server's structured one-click remedy for a rejected row:
 *   make_unit_divisible  → tick Divisible on the unit in Settings, re-check
 *   set_cells            → edit this row's cells (e.g. clear coverage), re-check
 *   add_alias            → "metre" reads as <chosen unit/category> from now on,
 *                          saved in Settings, whole batch re-checked */
const btnCls = "inline-flex items-center gap-1 px-2 py-0.5 rounded border border-accent/40 text-accent hover:bg-accent-soft disabled:opacity-50";

export function ImportRowFix({ row, lookups, fixing, onFix }) {
  const fix = row.fix;
  const [target, setTarget] = useState("");
  if (!fix) return null;
  const spinning = fixing === row.row_number;
  const Icon = spinning ? Loader2 : Wrench;
  const tid = `import-row-${row.row_number}-fix`;

  if (fix.type === "add_alias") {
    const list = (fix.kind === "unit" ? lookups?.units : lookups?.categories) || [];
    const live = list.filter((x) => x.status !== "archived");
    const norm = (s) => (s ?? "").toString().trim().toLowerCase().replace(/\s+/g, " ");
    // Added in Settings since this preview was checked? Then no alias is
    // needed — the batch just wants a re-check.
    const exists = live.find((x) => norm(x.label) === norm(fix.value) || norm(x.key) === norm(fix.value)
      || (x.aliases || []).some((a) => norm(a) === norm(fix.value)));
    if (exists) {
      return (
        <div className="mt-1 inline-flex items-center gap-1.5 flex-wrap" style={{ fontSize: 11 }} data-testid={`${tid}-alias`}>
          <span className="text-ink-3">'{fix.value}' is now in Settings ({exists.label}) —</span>
          <button type="button" onClick={() => onFix?.(row, { target_id: exists.id })} disabled={!!fixing} className={btnCls} data-testid={tid}>
            <Icon size={11} className={spinning ? "animate-spin" : ""} /> re-check preview
          </button>
        </div>
      );
    }
    return (
      <div className="mt-1 inline-flex items-center gap-1.5 flex-wrap" style={{ fontSize: 11 }} data-testid={`${tid}-alias`}>
        <span className="text-ink-3">Treat '{fix.value}' as</span>
        <select value={target} onChange={(e) => setTarget(e.target.value)} disabled={!!fixing}
          className="h-6 px-1 rounded border border-line bg-surface text-ink" style={{ fontSize: 11 }} data-testid={`${tid}-alias-target`}>
          <option value="">— choose a {fix.kind} —</option>
          {live.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
        </select>
        <button type="button" onClick={() => onFix?.(row, { target_id: target })} disabled={!!fixing || !target}
          className={btnCls} data-testid={tid} title={`Saves '${fix.value}' as an alias in Settings → Materials → ${fix.kind === "unit" ? "Units" : "Categories"} and re-checks every row`}>
          <Icon size={11} className={spinning ? "animate-spin" : ""} /> everywhere &amp; re-check
        </button>
      </div>
    );
  }
  const label = fix.type === "make_unit_divisible" ? `Make '${fix.unit_label}' divisible & re-check` : fix.label || "Fix & re-check";
  return (
    <button type="button" onClick={() => onFix?.(row)} disabled={!!fixing} className={"mt-1 " + btnCls} style={{ fontSize: 11 }} data-testid={tid}>
      <Icon size={11} className={spinning ? "animate-spin" : ""} /> {label}
    </button>
  );
}
